import { useCallback, useEffect, useState } from "react";
import type { KnowledgeFilter } from "@/lib/types";

const KEY_DEMO = "datacom.demoMode";
const KEY_FILTER = "datacom.knowledgeFilter";
const KEY_ENTERED = "datacom.entered";

/**
 * La versione, da package.json (vedi vite.config.ts).
 *
 * Era scritta a mano qui e diceva "0.1.0-demo": la parola "demo" sarebbe
 * finita nelle impostazioni di un app pubblicata sugli store. Ora c e un posto
 * solo da aggiornare, ed e quello che conta anche per il pacchetto.
 */
declare const __VERSIONE_APP__: string;
export const APP_VERSION = typeof __VERSIONE_APP__ === "string" ? __VERSIONE_APP__ : "sconosciuta";

function readBool(key: string, fallback: boolean) {
  if (typeof window === "undefined") return fallback;
  const v = localStorage.getItem(key);
  if (v === null) return fallback;
  return v === "true";
}

export function useDemoMode() {
  // Default OFF: i secrets sono configurati lato server (Lovable Cloud).
  const [demoMode, setDemoModeState] = useState<boolean>(() => readBool(KEY_DEMO, false));

  const setDemoMode = useCallback((v: boolean) => {
    setDemoModeState(v);
    try { localStorage.setItem(KEY_DEMO, String(v)); } catch { /* storage non disponibile: la scelta vale per questa sessione */ }
  }, []);

  return { demoMode, setDemoMode };
}

export function useKnowledgeFilter() {
  const [filter, setFilterState] = useState<KnowledgeFilter>(() => {
    if (typeof window === "undefined") return "all";
    const v = localStorage.getItem(KEY_FILTER);
    if (v === "ajax" || v === "daitem" || v === "all") return v;
    return "all";
  });

  const setFilter = useCallback((v: KnowledgeFilter) => {
    setFilterState(v);
    try { localStorage.setItem(KEY_FILTER, v); } catch { /* storage non disponibile: la scelta vale per questa sessione */ }
  }, []);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === KEY_FILTER && (e.newValue === "ajax" || e.newValue === "daitem" || e.newValue === "all")) {
        setFilterState(e.newValue);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  return { filter, setFilter };
}

export function useOnboarded() {
  const [entered, setEntered] = useState<boolean>(() => readBool(KEY_ENTERED, false));
  const markEntered = useCallback(() => {
    setEntered(true);
    try { localStorage.setItem(KEY_ENTERED, "true"); } catch { /* storage non disponibile: la scelta vale per questa sessione */ }
  }, []);
  const resetEntered = useCallback(() => {
    setEntered(false);
    try { localStorage.removeItem(KEY_ENTERED); } catch { /* storage non disponibile: la scelta vale per questa sessione */ }
  }, []);
  return { entered, markEntered, resetEntered };
}